import { useState } from 'react';
import type { BudgetRequestIn, KeyOut } from '../types';

interface BudgetRequestFormProps {
  keyItem: KeyOut;
  onSubmit: (data: BudgetRequestIn) => Promise<void>;
  onCancel: () => void;
  submitting: boolean;
}

/**
 * Form to request a temporary budget increase for a key. The increase is clamped
 * to the end of the current billing period by the backend.
 */
export function BudgetRequestForm({ keyItem, onSubmit, onCancel, submitting }: BudgetRequestFormProps) {
  const [deltaUsd, setDeltaUsd] = useState('');
  const [durationDays, setDurationDays] = useState('30');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const delta = parseFloat(deltaUsd);
    if (isNaN(delta) || delta <= 0) {
      setError('Increase amount must be greater than zero');
      return;
    }

    const duration = parseInt(durationDays, 10);
    if (isNaN(duration) || duration <= 0) {
      setError('Duration must be a positive number of days');
      return;
    }

    const data: BudgetRequestIn = {
      key_id: keyItem.id,
      delta_usd: delta,
      duration_days: duration,
      reason: reason.trim() || null,
    };

    try {
      await onSubmit(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const currentLimit = keyItem.limit_usd ?? 0;
  const delta = parseFloat(deltaUsd);
  const newLimit = isNaN(delta) ? currentLimit : currentLimit + delta;

  return (
    <form onSubmit={handleSubmit} className="create-key-form">
      <h3>Request temporary budget increase</h3>

      <div className="callout callout-warning">
        <strong>Temporary increase only:</strong> The increase will be clamped to the end of
        the current billing period ({keyItem.limit_reset ?? 'lifetime'}) and will automatically
        revert when the period resets.
      </div>

      <div className="form-group">
        <label htmlFor="key-name">Key</label>
        <input
          id="key-name"
          type="text"
          value={keyItem.name}
          disabled
          style={{ background: 'var(--bg)' }}
        />
      </div>

      <div className="form-group">
        <label htmlFor="delta">
          Increase amount <span className="required">*</span>
        </label>
        <div className="form-inline">
          <span>+</span>
          <input
            id="delta"
            type="number"
            value={deltaUsd}
            onChange={(e) => setDeltaUsd(e.target.value)}
            disabled={submitting}
            placeholder="0.00"
            step="0.01"
            min="0.01"
            style={{ width: '120px' }}
            required
          />
          <span>USD</span>
        </div>
        <p className="form-note">
          Current limit: <strong>${currentLimit.toFixed(2)}</strong> → New limit:{' '}
          <strong>${newLimit.toFixed(2)}</strong>
        </p>
      </div>

      <div className="form-group">
        <label htmlFor="duration">Requested duration</label>
        <div className="form-inline">
          <input
            id="duration"
            type="number"
            value={durationDays}
            onChange={(e) => setDurationDays(e.target.value)}
            disabled={submitting}
            min="1"
            style={{ width: '80px' }}
          />
          <span>days</span>
        </div>
        <p className="form-note warn">
          The backend will clamp this to the end of the current billing period.
        </p>
      </div>

      <div className="form-group">
        <label htmlFor="reason">Reason (optional)</label>
        <textarea
          id="reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={submitting}
          placeholder="Why do you need the extra budget?"
          rows={3}
          style={{ width: '100%', resize: 'vertical' }}
        />
        <p className="form-note">Help approvers understand the request.</p>
      </div>

      {error ? (
        <div className="form-error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="form-actions">
        <button type="button" onClick={onCancel} disabled={submitting}>
          Cancel
        </button>
        <button type="submit" className="primary" disabled={submitting}>
          {submitting ? 'Requesting...' : 'Submit request'}
        </button>
      </div>
    </form>
  );
}
